import { PageTitle, H2Heading } from "../../../components/Headers";
import { PageBody } from "../../../components/PageBody";
import { Helmet } from "react-helmet-async";
import { ArticleParagraph } from "../../../components/Paragraphs";
import { useEffect, useState, useCallback } from "react";
import { Loading } from "../../../components/Loading";
import { Link } from "react-router-dom";
import * as d3 from 'd3';
import { useChartDimensions } from "../../../components/graphs/useChartDimensions";
import { ChartCanvas } from "../../../components/graphs/basicGraph";

// New golf simulation imports
import { COURSE_PRESETS, generateCourseConfig } from "./utility/golfConfig";
import { simulateRound, simulateMultipleRounds } from "./utility/golfSimulation";
import { GolferConfig } from "./components/GolferConfig";
import { ScorecardView } from "./components/ScorecardView";
import { ScoreHeatmap, ProbabilitySummary } from "./components/ScoreHeatmap";
import { ScoringRidgePlot } from "./components/ScoringRidgePlot";


/**
 * Enhanced histogram with par/expected lines
 */
const ScoreHistogram = ({ simulationResults, course }) => {
    const [ref, dimensions] = useChartDimensions({
        marginTop: 30,
        marginRight: 20,
        marginBottom: 30,
        marginLeft: 40,
        height: 200
    });

    useEffect(() => {
        if (!simulationResults?.scores?.length || !dimensions.boundedWidth) return;

        const scores = simulationResults.scores;
        const bounds = d3.select(ref.current).select(".histogram-bounds");
        bounds.selectAll("*").remove();

        // Create scales with fixed bin size and extended domain
        const minScore = Math.floor(Math.min(...scores)) - 1;
        const maxScore = Math.ceil(Math.max(...scores)) + 1;
        const x = d3.scaleLinear()
            .domain([minScore, maxScore])
            .range([0, dimensions.boundedWidth]);

        // Calculate bar width with gap
        const fullBarWidth = (x(minScore + 1) - x(minScore));
        const barWidth = fullBarWidth * 0.98;

        // Generate histogram data with fixed bins
        const histogram = d3.histogram()
            .domain([minScore + 1, maxScore - 1])
            .thresholds(d3.range(minScore + 1, maxScore))
            .value(d => d);

        const bins = histogram(scores);

        const y = d3.scaleLinear()
            .domain([0, d3.max(bins, d => d.length)])
            .range([dimensions.boundedHeight, 0]);

        // Add bars
        bounds.selectAll("rect")
            .data(bins)
            .join("rect")
            .attr("x", d => x(d.x0) - barWidth / 2)
            .attr("width", barWidth)
            .attr("y", d => y(d.length))
            .attr("height", d => dimensions.boundedHeight - y(d.length))
            .attr("fill", "#62A87C")
            .attr("opacity", 0.7);

        // Add par line
        if (course.par >= minScore && course.par <= maxScore) {
            bounds.append("line")
                .attr("x1", x(course.par))
                .attr("x2", x(course.par))
                .attr("y1", dimensions.boundedHeight)
                .attr("y2", 0)
                .attr("stroke", "#22C55E")
                .attr("stroke-width", 2)
                .attr("stroke-dasharray", "4,4");

            bounds.append("text")
                .attr("x", x(course.par) + 4)
                .attr("y", 12)
                .attr("fill", "#22C55E")
                .attr("font-size", "10px")
                .attr("font-weight", "bold")
                .text("Par");
        }

        // Add expected score line with flag
        const expectedScore = simulationResults.expectedScore;
        const flagPoleTop = 0;

        bounds.append("line")
            .attr("x1", x(expectedScore))
            .attr("x2", x(expectedScore))
            .attr("y1", dimensions.boundedHeight)
            .attr("y2", flagPoleTop)
            .attr("stroke", "red")
            .attr("stroke-width", 2);

        // Add circular flag base
        bounds.append("circle")
            .attr("cx", x(expectedScore))
            .attr("cy", dimensions.boundedHeight)
            .attr("r", 3)
            .attr("fill", "red");

        // Add flag
        const flagWidth = 15;
        const flagHeight = 10;
        bounds.append("path")
            .attr("d", `M ${x(expectedScore) - 1} ${flagPoleTop}
                       L ${x(expectedScore) - 1} ${flagPoleTop + flagHeight}
                       L ${x(expectedScore) - 1 - flagWidth} ${flagPoleTop + flagHeight / 2}
                       L ${x(expectedScore) - 1} ${flagPoleTop}`)
            .attr("fill", "red");

        // Add best/worst 10% lines
        if (simulationResults.best10Percentile) {
            bounds.append("line")
                .attr("x1", x(simulationResults.best10Percentile))
                .attr("x2", x(simulationResults.best10Percentile))
                .attr("y1", dimensions.boundedHeight)
                .attr("y2", 0)
                .attr("stroke", "#3B82F6")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "2,2")
                .attr("opacity", 0.7);

            bounds.append("text")
                .attr("x", x(simulationResults.best10Percentile) + 2)
                .attr("y", dimensions.boundedHeight - 8)
                .attr("fill", "#3B82F6")
                .attr("font-size", "8px")
                .text("Best 10%");
        }

        if (simulationResults.worst10Percentile) {
            bounds.append("line")
                .attr("x1", x(simulationResults.worst10Percentile))
                .attr("x2", x(simulationResults.worst10Percentile))
                .attr("y1", dimensions.boundedHeight)
                .attr("y2", 0)
                .attr("stroke", "#EF4444")
                .attr("stroke-width", 1)
                .attr("stroke-dasharray", "2,2")
                .attr("opacity", 0.7);

            bounds.append("text")
                .attr("x", x(simulationResults.worst10Percentile) + 2)
                .attr("y", dimensions.boundedHeight - 8)
                .attr("fill", "#EF4444")
                .attr("font-size", "8px")
                .text("Worst 10%");
        }

        // Add axes
        const totalTicks = maxScore - minScore - 1;
        const maxTickCount = 15;
        const tickStep = Math.ceil(totalTicks / maxTickCount);

        const xAxis = d3.axisBottom(x)
            .tickValues(
                d3.range(minScore + 1, maxScore)
                    .filter(d => d % tickStep === 0)
            )
            .tickFormat(d => Math.round(d));

        const yAxis = d3.axisLeft(y)
            .ticks(5);

        bounds.append("g")
            .attr("transform", `translate(0,${dimensions.boundedHeight})`)
            .call(xAxis);

        bounds.append("g")
            .call(yAxis);

    }, [simulationResults, course, dimensions, ref]);

    return (
        <div className="flex flex-col items-center mt-8 w-full" ref={ref}>
            <H2Heading>Score Distribution</H2Heading>
            <ChartCanvas dimensions={dimensions}>
                <g className="histogram-bounds" />
            </ChartCanvas>
        </div>
    );
};


/**
 * Score distribution with horizontal bars — colour encodes relative to par
 */
const ScoreDistributionBars = ({ scoreDistribution, course }) => {
    const maxPercentage = Math.max(...scoreDistribution.map(info => info.percentage));
    const par = course.par;

    const getBarColour = (score) => {
        const diff = score - par;
        if (diff <= -3) return '#065F46';
        if (diff === -2) return '#047857';
        if (diff === -1) return '#059669';
        if (diff === 0) return '#10B981';
        if (diff === 1) return '#F59E0B';
        if (diff === 2) return '#F97316';
        if (diff === 3) return '#EF4444';
        return diff < 0 ? '#065F46' : '#DC2626';
    };

    return (
        <div className="mt-4 space-y-0.5">
            {scoreDistribution.map(({ score, percentage }) => {
                const barWidth = maxPercentage > 0 ? (percentage / maxPercentage) * 100 : 0;
                const colour = getBarColour(score);
                const diff = score - par;
                const diffLabel = diff === 0 ? 'E' : diff > 0 ? `+${diff}` : String(diff);

                return (
                    <div key={score} className="flex items-center gap-2 text-xs">
                        <span className="w-8 text-right font-mono text-gray-600 tabular-nums">{score}</span>
                        <span className={`w-6 text-right font-mono tabular-nums ${
                            diff < 0 ? 'text-green-700' : diff > 0 ? 'text-red-600' : 'text-gray-500'
                        }`}>
                            {diffLabel}
                        </span>
                        <div className="flex-1 h-4 bg-gray-100 rounded-sm overflow-hidden">
                            <div
                                className="h-full rounded-sm transition-all"
                                style={{
                                    width: `${barWidth}%`,
                                    backgroundColor: colour,
                                    opacity: 0.75
                                }}
                            />
                        </div>
                        <span className="w-12 text-right font-mono text-gray-500 tabular-nums">
                            {percentage.toFixed(1)}%
                        </span>
                    </div>
                );
            })}
        </div>
    );
};


/**
 * Simulation statistics display
 */
const SimulationStats = ({ results }) => {
    if (!results) return null;

    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 text-center">
            <div className="bg-green-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Average</div>
                <div className="text-xl font-bold text-green-700">{results.avgScore}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Median</div>
                <div className="text-xl font-bold text-gray-700">{results.median}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Best 10%</div>
                <div className="text-xl font-bold text-blue-700">{results.best10Percentile}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">Worst 10%</div>
                <div className="text-xl font-bold text-red-700">{results.worst10Percentile}</div>
            </div>
        </div>
    );
};


export const GolfRound = () => {
    // State for golfer configuration
    const [handicap, setHandicap] = useState(10);
    const [course, setCourse] = useState(generateCourseConfig(COURSE_PRESETS.medium));
    const [consistency, setConsistency] = useState(0.25);

    // State for simulation results
    const [simulationResults, setSimulationResults] = useState(null);
    const [sampleRound, setSampleRound] = useState(null);
    // Run simulation when configuration changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            // Run batch simulation
            const results = simulateMultipleRounds(handicap, course, 500, consistency);

            // Run a single sample round for the scorecard
            const sample = simulateRound(handicap, course, consistency);

            setSimulationResults(results);
            setSampleRound(sample);
        }, 100);

        return () => clearTimeout(timeoutId);
    }, [handicap, course, consistency]);

    // Handler to simulate a new sample round
    const handleNewRound = useCallback(() => {
        const sample = simulateRound(handicap, course, consistency);
        setSampleRound(sample);
    }, [handicap, course, consistency]);

    if (simulationResults === null) {
        return <Loading />;
    }

    return (
        <PageBody>
            <Helmet>
                <title>Golf Round Simulation | VizBadger</title>
                <meta
                    name="description"
                    content="Simulate golf rounds using the handicap system and hole-by-hole scoring probabilities. Understand how handicap, course difficulty, and consistency affect scoring."
                />
                <meta name="keywords" content="Golf simulation, handicap calculator, golf scoring, Monte Carlo simulation, golf statistics, course handicap, stroke play" />
            </Helmet>

            <PageTitle title="Golf Round Simulation" />

            <ArticleParagraph
                lines={[
                    "This simulation models golf rounds using genuine golf terminology and mechanics. Instead of simple averages, it uses the official handicap system, course difficulty ratings, and hole-by-hole scoring probabilities derived from real golf statistics.",
                    "The handicap index represents a golfer's overall ability (0 = scratch, 10 = mid-handicapper, 20+ = high handicapper). Combined with course difficulty (slope and rating), this determines the expected score for each round.",
                    "Each hole is simulated independently using probability distributions that vary by hole type (par 3, 4, or 5) and golfer handicap. This produces realistic scoring patterns including the occasional birdie and the inevitable triple bogey."
                ]}
            />

            <H2Heading>Golfer Configuration</H2Heading>
            <ArticleParagraph
                lines={[
                    "Adjust the handicap to see how scoring distributions change. Expand the course and advanced settings for more control over the simulation."
                ]}
            />

            <GolferConfig
                handicap={handicap}
                onHandicapChange={setHandicap}
                course={course}
                onCourseChange={(c) => setCourse(generateCourseConfig(c))}
                consistency={consistency}
                onConsistencyChange={setConsistency}
            />

            {/* Sample round scorecard */}
            <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                    <H2Heading>Sample Round</H2Heading>
                    <button
                        onClick={handleNewRound}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colours text-sm font-medium"
                    >
                        Simulate New Round
                    </button>
                </div>
                <ScorecardView round={sampleRound} course={course} />
            </div>

            {/* Probability heatmap */}
            <div className="mt-8">
                <H2Heading>Scoring Probabilities by Hole</H2Heading>
                <p className="text-sm text-gray-600 mb-4">
                    This heatmap shows the probability of each score type for every hole.
                    Hover over cells to see exact percentages.
                </p>
                <ScoreHeatmap
                    handicap={handicap}
                    course={course}
                    consistency={consistency}
                />
            </div>

            {/* Scoring distribution ridge plot */}
            <div className="mt-8">
                <H2Heading>Score Distributions by Handicap</H2Heading>
                <p className="text-sm text-gray-600 mb-4">
                    How the spread of scores shifts as handicap increases. Each ridge shows the
                    distribution of scores relative to par for that handicap bracket.
                </p>
                <ScoringRidgePlot course={course} consistency={consistency} />
            </div>

            {/* Expected scoring breakdown */}
            <div className="mt-8">
                <ProbabilitySummary
                    handicap={handicap}
                    course={course}
                    consistency={consistency}
                />
            </div>

            {/* Simulation results */}
            <div className="mt-8">
                <H2Heading>Simulation Results (500 Rounds)</H2Heading>
                <SimulationStats results={simulationResults} />
            </div>

            {/* Histogram and distribution table */}
            <div className="flex flex-col-reverse lg:flex-row gap-8 mt-4">
                <div className="lg:w-1/3">
                    <ScoreDistributionBars
                        scoreDistribution={simulationResults.scoreDistribution}
                        course={course}
                    />
                </div>
                <div className="lg:w-2/3">
                    <ScoreHistogram
                        simulationResults={simulationResults}
                        course={course}
                    />
                </div>
            </div>

            {/* Methodology note */}
            <div className="mt-8 p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
                <strong>Methodology:</strong> Scoring probabilities are based on published
                golf statistics for different handicap levels. The simulation uses hole-by-hole
                probability sampling rather than a simple normal distribution, producing
                more realistic score patterns.{' '}
                <Link
                    to="/sports-models/golf/methodology"
                    className="text-green-600 hover:text-green-800 underline"
                >
                    Learn more about how this simulation works &rarr;
                </Link>
            </div>
        </PageBody>
    );
};
